/**
 *
 * Doc is a studio document.
 *
 * Note that this file is deliberately implemented only with import type declarations
 * as this file is very commonly imported and we don't want this to get out of control.
 *
 */
import type { ComponentOwnedMemberEntityType, GroupableEntityType } from "building-types/handles/docEntityHandleTypes";
import type { Table } from "common/data/table";
import type { Annotation } from "./annotation/annotationTypes";
import type {
  DocFacadeSpanStratumAssociation,
  DocFacadeSweepStratumAssociation,
  DocDatumSpanAssociation,
} from "./associations/associationTypes";
import type { Block } from "./blocks/blockTypes";
import type { HomeAttributes } from "./building/homeAttributes";
import type { WallCasework } from "./casework/wallCaseworkTypes";
import type { Ceiling } from "./ceilings/ceiling";
import type { Column } from "./columns/columnTypes";
import type { ComponentOptionSetAssociation } from "./components/componentAssociationTypes";
import type { ComponentInstance } from "./components/componentInstanceTypes";
import type { Component } from "./components/componentTypes";
import type { DataSequence } from "./dataSequences/dataSequence";
import type { DataTable } from "./dataTables/dataTable";
import type { DatumEntity } from "./datums/datumEntityTypes";
import type { DocForeignHashJoinTable, DocForeignHashTable } from "./doc/docForeignKeyTable";
import type { DocRef } from "./docRef";
import type { EdgeBehavior } from "./edgeBehavior/edgeBehavior";
import type { ElectricalDevice } from "./electrical/electricalDeviceTypes";
import type { InPlaceElectricalLayout } from "./electrical/electricalInPlaceLayoutTypes";
import type { Asset } from "./entities/asset";
import type { EntityRef } from "./entities/entityRefTypes";
import type { Label } from "./entities/labelTypes";
import type { Measurement } from "./entities/measurementTypes";
import type { ProfileEntity } from "./entities/profileEntityTypes";
import type { ReferenceGeom } from "./entities/referenceGeom";
import type { ReportableUnitDesignation } from "./entities/reportableUnitDesignationTypes";
import type { Underlay } from "./entities/underlay";
import type { EntitySettings } from "./entitySettings/entitySettingsTypes";
import type { Facade, FacadeSweepStratum, FacadeSpanStratum } from "./facades/facadeTypes";
import type { CipComponentEntity } from "./floors/cipComponentTypes";
import type { FloorAssemblyOverride } from "./floors/floorAssemblyTypes";
import type { FloorAssemblyFinishLayerOverride } from "./floors/floorFinishTypes";
import type { FloorSystemExternal } from "./floors/floorSystemTypes";
import type { FrameFloorAssemblyEntity } from "./floors/frameFloorAssemblyTypes";
import type { MasonryComponentEntity } from "./floors/masonryComponentTypes";
import type { SheathingComponentEntity } from "./floors/sheathingComponentTypes";
import type { SlabComponentEntity } from "./floors/slabComponentTypes";
import type { SlabFloorAssemblyEntity } from "./floors/slabFloorAssemblyTypes";
import type { FoundationAssemblyEntity } from "./foundations/foundationAssemblyEntityTypes";
import type { FoundationComponentEntity } from "./foundations/foundationComponentEntityTypes";
import type { FoundationSystem, Foundation } from "./foundations/foundationSystemTypes";
import type { FramingComponentEntity } from "./framing/framingComponentTypes";
import type { HeaderAssemblyEntity } from "./framing/headerAssemblyTypes";
import type { Group } from "./groups/group";
import type { HidableEntityHandle, LockableEntityHandle } from "./handles/docEntityHandleTypes";
import type { ForeignEntityId } from "./handles/foreignEntityId";
import type { CompositeLevel } from "./levels/CompositeLevel";
import type { LevelDef } from "./levels/LevelDef";
import type { Palette } from "./materials/palette";
import type { EdgeModifier } from "./modifiers/edgeModifierTypes";
import type { Modifier } from "./modifiers/modifierTypes";
import type { Opening } from "./openings/opening";
import type { DocOptionRule } from "./options/docOptionRule";
import type { OptionChoices } from "./options/optionChoices";
import type { DocOptionEffect } from "./options/optionEffect";
import type { DocOptionSet, DocOptionLock, InstanceOptionSet } from "./options/optionSet";
import type { DocOptionTemplate, DocOptionSetOverrides } from "./options/optionTemplateTypes";
import type { GlobalParameter, LocalParameter } from "./options/parameter";
import type { Preset } from "./options/preset";
import type { PropInstance } from "./props/propInstanceTypes";
import type { PropAttributes } from "./props/propTypes";
import type {
  RoofSystem,
  Roof,
  RoofOpeningPlacement,
  RoofDormerPlacement,
  SkylightPlacement,
} from "./roofs/roofSystemTypes";
import type { RoofVolume } from "./roofVolumes/roofVolumeTypes";
import type { RoomLayout, RoomLayoutEntityType, RoomLayoutInstance } from "./roomLayouts/roomLayoutTypes";
import type { StaticRoom } from "./rooms/staticRoomTypes";
import type { RuleBasedSelector, RuleBasedSelectorInstance } from "./selectors/ruleBasedSelectorTypes";
import type { ViewOverride, Selector } from "./selectors/selectorTypes";
import type { ViewSelector } from "./selectors/viewSelectorTypes";
import type { Railing, PostPlacement, RailingDefaultSetting } from "./settings/railingTypes";
import type { SettingsFragment } from "./settings/settingsFragment";
import type { SettingsSet } from "./settings/settingsSet";
import type { Site } from "./site/site";
import type { SiteShape } from "./site/siteShape";
import type { StairSetting, StairSystem, StairBlock } from "./stairs/stairTypes";
import type { Effect } from "./views/effectTypes";
import type { Elevation } from "./views/elevationTypes";
import type {
  PlanSetTemplate,
  PlanSetTemplateConfig,
  ContentTemplate,
  ContentTemplateInstance,
  ContentReplicator,
  Viewport,
  ViewTemplate,
  ViewTemplateOverride,
} from "./views/planSets";
import type { Sheet, DrawingRef } from "./views/sheetsTypes";
import type { SnapEntity } from "./views/snapEntity";
import type { SnapKind } from "./views/snapKind";
import type { View3D, PlanView, ElevationView, SectionView } from "./views/View3D";
import type { FreeformWallEntity } from "./walls2/freeformWallTypes";
import type { WallPlaneAssemblyEntity } from "./walls2/wallPlaneAssemblyTypes";
import type { DocWallPlaneFilter } from "./walls2/wallPlaneFilters";
import type { WallPlaneRuleEntity } from "./walls2/wallPlaneRules";
import type { WallPlaneTrimEntity } from "./walls2/wallPlaneTrimTypes";

/**
 * Shared capacity for module-level caches keyed on {@link Doc} identity (e.g. the LRUs in
 * `docCollectSystemIds`, `docEntityNodeSchemaUtils`, `docCollectEntityReferences`).
 *
 * Sized at 4× the realistic concurrent-doc count for a studio session (current doc +
 * doc-ref + a few transients) and aligned with the other Doc-keyed caches in
 * `packages/doc` (DocEntityNodeRegistry outer doc LRU, parentChildCache, etc. — all 2–4).
 * Per-doc node caches inside {@link DocEntityNodeRegistry} are capped separately by
 * {@link DOC_ENTITY_NODE_CACHE_SIZE}. Larger outer values retain MB-scale Doc snapshots
 * retain MB-scale Doc snapshots strongly without a corresponding hit-rate gain.
 *
 * Lifetime semantics — important: the consumers are LRU caches with **strong** references
 * to their keys, not WeakMaps. A Doc evicted from the LRU becomes collectible; a Doc
 * still resident in the LRU is pinned in memory even if nothing else references it. This
 * is acceptable at this small capacity (at most {@link DOC_CACHE_SIZE} stale docs can be
 * retained), but it is a different lifetime model from `reverseReferenceIndexCache` in
 * `docEntityNodeBase.ts`, which uses a WeakMap and releases doc-keyed entries eagerly.
 * Don't bump this constant without re-evaluating the worst-case retained-memory cost.
 */
export const DOC_CACHE_SIZE = 16;

/**
 * Capacity for Doc-keyed LRU caches that track a small set of concurrently loaded doc
 * snapshots (local doc, pinned CDM doc, previous CDM version during sync, etc.).
 * Shared by {@link DocEntityNodeRegistry}, doc ref eligible-entity/roots caches, and
 * parent-child caches in `docEntityForEachChild`.
 */
export const DOC_CONCURRENT_SNAPSHOT_CACHE_SIZE = 4;

/**
 * Max per-doc entries in handle-keyed caches keyed on {@link Doc} (e.g.
 * {@link DocEntityNodeRegistry} and `validateDocEntityNodeReferences`). Entries are cheap
 * to rebuild from `(doc, handle)` but accumulate quickly during full-doc traversals;
 * bounding these maps prevents unbounded growth in long studio sessions.
 *
 * Worst-case retention for the node registry is roughly `DOC_ENTITY_NODE_CACHE_SIZE ×`
 * {@link DOC_CONCURRENT_SNAPSHOT_CACHE_SIZE}. Reference-validation results share this cap
 * per doc via {@link DOC_CACHE_SIZE} outer doc entries.
 */
export const DOC_ENTITY_NODE_CACHE_SIZE = 2048;

/**
 * Doc contains user-authored content from Studio. It is also one of Studio's runtime data structures.
 * Docs can be stored in a db or saved as .home files.
 */
export interface Doc {
  /**
   * A field that defines whether this document is parsed or not.
   */
  readonly isParsed: boolean;

  /**
   * A version string for the format stored in MAJOR.MINOR.BUILD form.
   */
  readonly schemaVersion: string;

  /**
   * A user-friendly name for the file
   */
  readonly name: string;

  /**
   * The set of entities that are locked. This typically is only relevant
   * for data types that can be moved in the EditorViewer.
   */
  readonly locked: DocForeignHashTable<LockableEntityHandle["type"], LockableEntityHandle>;

  /**
   * The set of entities that are hidden. This typically is only relevant
   * for data types that can be moved in the EditorViewer.
   */
  readonly hidden: DocForeignHashTable<HidableEntityHandle["type"], HidableEntityHandle>;

  /**
   * The kinds of snapping that are enabled in the EditorViewer. For example,
   * endpoint, midpoint, etc.
   */
  readonly snapKinds: readonly SnapKind[];

  /**
   * The types of entities that can be snapped to in the EditorViewer.
   */
  readonly snapEntities: readonly SnapEntity[];

  /**
   * The tolerance for grid snapping in the EditorViewer. This can ba adjusted by
   * the user.
   */
  readonly gridSnapDistance: number;

  /**
   * Reusable external assets like images, DXF files, etc...
   */
  readonly assets: Table<Asset>;

  /**
   * Image underlays for the EditorViewer.
   */
  readonly underlays: Table<Underlay>;

  /**
   * Roof plan underlays. Stored separately from floor plan underlays.
   */
  readonly roofUnderlays: Table<Underlay>;

  /**
   * Presets are essentially annotated OptionChoices objects that are stored for quick recall
   * by the user.
   */
  readonly presets: Table<Preset>;

  /**
   * The array of levels. Note that entities in the Doc may have formulas that allow them
   * to be enabled or disabled so each level may not appear in the building.
   */
  readonly levels: Table<LevelDef>;

  /**
   * Composite levels group multiple levels together for combined rendering
   * on plan views and viewports.
   */
  readonly compositeLevels: Table<CompositeLevel>;

  /**
   * Blocks are groups of rooms that can be enabled or disabled. For example, a master suite.
   */
  readonly blocks: Table<Block>;

  /**
   * Rooms are essentially polygons whose edges are annotated with openings, hosted entities, etc.
   */
  readonly rooms: Table<StaticRoom>;

  /**
   * A Room Layout enables sharing entities from one room to n others.
   */
  readonly roomLayouts: Table<RoomLayout>;

  /**
   * Room layout instance is a room layout that has been applied to a room.
   */
  readonly roomLayoutInstances: Table<RoomLayoutInstance>;

  /**
   * Key value pairs that map an room-layout-eligible-entity to a room layout
   */
  readonly entityRoomLayouts: DocForeignHashJoinTable<RoomLayoutEntityType, ForeignEntityId<"ROOM_LAYOUT">>;

  /**
   * Room Specific Floor Assemblies. These override the inherited Level Floor Assembly
   */
  readonly floors: Table<FloorAssemblyOverride>;

  /**
   * Room Specific Floor Finishes. These override the inherited Room type assignment
   */
  readonly floorFinishes: Table<FloorAssemblyFinishLayerOverride>;

  /**
   * Room specific column sets.
   */
  readonly columns: Table<Column>;

  /**
   * Openings on room edges
   */
  readonly openings: Table<Opening>;

  /**
   * Loads and controllers for electrical.
   */
  readonly electricalDevices: Table<ElectricalDevice>;

  /**
   * Inplace electrical layouts similar to those in electrical settings
   */
  readonly electricalLayouts: Table<InPlaceElectricalLayout>;

  /**
   * StairSystems are collections of stair blocks that together
   */
  readonly stairSystems: Table<StairSystem>;

  /**
   * StairBlocks are, for example, runs of stairs. They don't exist outside of
   * StairSystems.
   */
  readonly stairBlocks: Table<StairBlock>;

  /**
   * The catalog of reusable stair settings that a StairSystem can reference via
   * StairSystem.stairSettingId. Replaces the legacy STAIR_CATALOG_SETTINGS settings-backed catalog.
   */
  readonly stairCatalog: Table<StairSetting>;

  /**
   * User-authored railings (eg for the perimeter of a porch)
   */
  readonly railings: Table<Railing>;

  /**
   * User-authored posts for railings
   */
  readonly railingPosts: Table<PostPlacement>;

  /**
   * RoofSystems are collections of Roofs. The only
   * kind that can be created and edited in studio are multi gable roofs.
   */
  readonly roofSystems: Table<RoofSystem>;

  /**
   * RoofSystems are collections of Roofs. The only
   * kind that can be created and edited in studio are multi gable roofs.
   */
  readonly roofs: Table<Roof>;

  /**
   * Roof volumes
   */
  readonly roofVolumes: Table<RoofVolume>;

  /**
   * FoundationSystems are collections of Foundations.
   */
  readonly foundationSystems: Table<FoundationSystem>;

  /**
   * A Foundation is a part of a FoundationSystem. They can't exist outside
   * of a FoundationSystem.
   */
  readonly foundations: Table<Foundation>;

  /**
   * Behaviors that are assigned to edges of rooms and stairblocks.
   */
  readonly edgeBehaviors: Table<EdgeBehavior>;

  /**
   * Wall plane assemblies define layered wall build-ups for wall plane operations.
   */
  readonly wallPlaneAssemblies: Table<WallPlaneAssemblyEntity>;

  /**
   * Portable face-node filter catalog entries referenced by wall face rules.
   */
  readonly wallPlaneFilters: Table<DocWallPlaneFilter>;

  /**
   * Building graph rules combine filters with a single operator and optional AABB.
   */
  readonly wallPlaneRules: Table<WallPlaneRuleEntity>;

  /**
   * Profile sweeps applied to wall planes via wall plane rules.
   * Optional finish selects exterior vs interior material.
   */
  readonly wallPlaneTrims: Table<WallPlaneTrimEntity>;

  /**
   * Freeform walls are polylines (open or closed) unassociated with Rooms.
   * Older docs may omit this table at runtime; callers should use `doc.freeformWalls ?? {}`
   * (same pattern as wallPlaneTrims — no migration required).
   */
  readonly freeformWalls: Table<FreeformWallEntity>;

  /**
   * An Elevation is a style of a building that is significantly different
   * while preserving the
   */
  readonly elevations: Table<Elevation>;

  /**
   * The Facades in the building. These are assigned to room edges using "labels"
   */
  readonly facades: Table<Facade>;

  /**
   * The collection of FacadeSweepStratum. These are assigned to Facades.
   */
  readonly facadeSweepStrata: Table<FacadeSweepStratum>;

  /**
   * The collection of FacadeSpanStratum. These are assigned to Facades.
   */
  readonly facadeSpanStrata: Table<FacadeSpanStratum>;

  /**
   * ReferenceGeom is simple geometry that can be used to sketch other parts of the
   * building.
   */
  readonly referenceGeom: Table<ReferenceGeom>;

  /**
   * User-defined sheets that can be appended to the PDF drawing set.
   */
  readonly sheets: Table<Sheet>;

  /**
   * Drawings that can be associated with rooms or sheets.
   */
  readonly drawingRefs: Table<DrawingRef>;

  /**
   * Interior and exterior palettes that can be referenced by a Exterior & Interior
   * Material Settings or referenced from this document.
   */
  readonly palettes: Table<Palette>;

  /**
   * Annotation includes dimensions, leaders, and linework that can be added in addition
   * to automated dimensions. These are associated with rooms.
   */
  readonly annotation: Table<Annotation>;

  /**
   * Casework are cabinets, shelves, etc but can be used in quite a lot of scenarios.
   * These are associated with rooms.
   */
  readonly casework: Table<WallCasework>;

  /**
   * Special ceiling behaviors for rooms.
   */
  readonly ceilings: Table<Ceiling>;

  /**
   * Props are 3D models that supplement the building.
   */
  readonly propInstances: Table<PropInstance>;

  /**
   * Describes general attributes of a prop. These are used to create PropInstances.
   */
  readonly propAttributes: Table<PropAttributes>;

  /**
   * Centralized profile catalog entities.
   */
  readonly profiles?: Table<ProfileEntity>;

  /**
   * Centralized railing catalog entities.
   * @todo make required during migration step
   */
  readonly railingCatalog?: Table<RailingDefaultSetting>;

  /**
   * Openings in Roofs
   */
  readonly roofOpenings: Table<RoofOpeningPlacement>;

  /**
   * Dormers attached to Roofs.
   */
  readonly dormers: Table<RoofDormerPlacement>;

  /**
   * Skylight penetrations in Roofs.
   */
  readonly skylights: Table<SkylightPlacement>;

  /**
   * Modifiers allow changing the position of a collection of entities.
   */
  readonly modifiers: Table<Modifier>;

  /**
   * Edge Modifiers assign a modifier to an edge or edges of an entity.
   */
  readonly edgeModifiers: Table<EdgeModifier>;

  /**
   * View Overrides allow overriding the appearance of entities in a view.
   */
  // TODO: merge this with effects
  readonly viewOverrides: Table<ViewOverride>;

  /**
   * Selectors are used to select entities in a view to apply a view override to.
   */
  readonly selectors: Table<Selector>;

  /**
   * ViewSelectors are used to select a view to apply a view override to.
   */
  readonly viewSelectors: Table<ViewSelector>;

  /**
   * Measurements measure spatial qualities of the building like area
   * and length.
   */
  readonly measurements: Table<Measurement>;

  /**
   * Groups allow you to organize entities into groups.
   */
  readonly groups: Table<Group>;

  /**
   * This table maps the doc entity handle hash to the group id
   * it is associated with. Hence, any entity type can be grouped.
   */
  readonly entityGroups: DocForeignHashJoinTable<GroupableEntityType, ForeignEntityId<"GROUP">>;

  /**
   * Maps entities to their parent component. Each entity can be assigned to only one component.
   * Keys are entity IDs, values are component IDs.
   */
  readonly componentMembers: DocForeignHashJoinTable<ComponentOwnedMemberEntityType, ForeignEntityId<"COMPONENT">>;

  /**
   * Associations between entities and component definitions. In contrast to componentMembers,
   * these entities can be associated with multiple component definitions.
   */
  readonly componentAssociations: Table<ComponentOptionSetAssociation>;

  /**
   * Components allow non-exclusive, nestable organization of entities with transformable instances.
   */
  readonly components: Table<Component>;

  /**
   * Instances of components; each instance applies transforms and injects members into generation.
   */
  readonly componentInstances: Table<ComponentInstance>;

  /**
   * Document references that allow the user to reference entities like
   * settings, options, and palettes from other documents.
   */
  readonly docRefs: Table<DocRef>;

  /**
   * User defined Site model
   */
  readonly sites: Table<Site>;

  /**
   * User defined shapes used to generate Site
   */
  readonly siteShapes: Table<SiteShape>;

  /**
   * These are the specific entities that are imported into this document
   * from document references.
   */
  readonly entityRefs: Table<EntityRef>;

  /**
   * SettingsSets are groups of Settings. They can be conditionally enabled.
   */
  readonly settingsSets: Table<SettingsSet>;

  /**
   * Settings are child entities of SettingsSets. They can't exist outside of
   * SettingsSets.
   */
  readonly settings: Table<SettingsFragment>;

  readonly entitySettings: Table<EntitySettings>;

  /**
   * An DocOptionTemplate stores overrides for option sets and options. Each
   * override can be enabled/disabled.
   */
  readonly optionTemplates: Table<DocOptionTemplate>;

  /**
   * Overrides for a DocOptionSet. The parentId in a DocOptionSetOverrides
   * points at an DocOptionTemplate in the table above. A DocOptionTemplate
   * can be viewed as a container for select entities in this table.
   */
  readonly optionSetOverrides: Table<DocOptionSetOverrides>;

  /**
   * Option Sets contain their own metadata, as well as child Options.
   * The content within a DocOptionSet must not be tightly coupled to any
   * one particular document. The content must still make sense if ported
   * to a different document.
   */
  readonly optionSets: Table<DocOptionSet>;

  /**
   * Instance option sets join archetype option sets to component instances.
   */
  readonly instanceOptionSets: Table<InstanceOptionSet>;

  /**
   * Option Rules defines a trigger condition that other option effects
   * reference. When the trigger condition is true, the option effects
   * resolve.
   */
  readonly optionRules: Table<DocOptionRule>;

  /**
   * Option Effects describe changes to options that occur when an option
   * rule is true.
   */
  readonly optionEffects: Table<DocOptionEffect>;

  /**
   * Conditions for locking option sets and options.
   * Copied from auxOptionSets so that these can be doc ref'd independently.
   * Currently enabled via docRefOptionLocks feature flag
   */
  readonly optionLocks: Table<DocOptionLock>;

  /**
   * The options currently set in the "Active" panel in Studio.
   */
  readonly optionChoices: OptionChoices;

  /**
   * Used for storing static attributes of a home
   */
  readonly attributes: HomeAttributes;

  /**
   * 3D views of the building
   */
  readonly view3Ds: Table<View3D>;

  /**
   * Plan views
   */
  readonly planViews: Table<PlanView>;

  /**
   * Elevation views
   */
  readonly elevationViews: Table<ElevationView>;

  /**
   * Section views
   */
  readonly sectionViews: Table<SectionView>;

  /**
   * Datums
   */
  readonly datums: Table<DatumEntity>;

  /**
   * Header Assemblies
   */
  readonly headerAssemblies: Table<HeaderAssemblyEntity>;

  /**
   * Framing Components
   */
  readonly framingComponents: Table<FramingComponentEntity>;

  /**
   * Masonry Components (wall plane assemblies).
   */
  readonly masonryComponents: Table<MasonryComponentEntity>;

  /**
   * Cast-in-place (CIP) concrete components for wall plane assemblies.
   */
  readonly cipComponents: Table<CipComponentEntity>;

  /**
   * Sub Floor Components
   */
  readonly sheathingComponents: Table<SheathingComponentEntity>;

  /**
   * Foundation Assemblies
   */
  readonly foundationAssemblies: Table<FoundationAssemblyEntity>;

  /**
   * Foundation Components
   */
  readonly foundationComponents: Table<FoundationComponentEntity>;

  /**
   * Floor Assemblies
   */
  readonly frameFloorAssemblies: Table<FrameFloorAssemblyEntity>;

  /**
   * Slab Floor Assemblies
   */
  readonly slabFloorAssemblies: Table<SlabFloorAssemblyEntity>;

  /**
   * Slab Components
   */
  readonly slabComponents: Table<SlabComponentEntity>;

  /**
   * User-defined plan set entities
   */
  readonly planSetTemplates: Table<PlanSetTemplate>;
  readonly planSetTemplateConfigs: Table<PlanSetTemplateConfig>;
  readonly contentTemplates: Table<ContentTemplate>;
  readonly contentTemplateInstances: Table<ContentTemplateInstance>;
  readonly contentReplicators: Table<ContentReplicator>;
  readonly viewports: Table<Viewport>;
  readonly viewTemplates: Table<ViewTemplate>;
  readonly viewTemplateOverrides: Table<ViewTemplateOverride>;

  /**
   * Local parameters are children to content templates and plan set templates and
   * only available to instances of their parent templates.
   */
  readonly localParameters: Table<LocalParameter>;

  /**
   * Global parameters are available to any instance of a content template or plan set template.
   */
  readonly globalParameters: Table<GlobalParameter>;
  readonly dataSequences: Table<DataSequence>;
  readonly dataTables: Table<DataTable>;
  readonly effects: Table<Effect>;

  readonly labels: Table<Label>;

  /**
   * Reportable unit designations link labels to user-visible names for reportable units.
   */
  readonly reportableUnitDesignations: Table<ReportableUnitDesignation>;

  /**
   * Allow for multiple filter rules to be combined into a single selector.
   */
  readonly ruleBasedSelectors: Table<RuleBasedSelector>;

  /**
   * Instances of ruleBasedSelectors that are applied to view template overrides. Children to viewTemplateOverrides.
   */
  readonly ruleBasedSelectorInstances: Table<RuleBasedSelectorInstance>;

  /**
   * Associations between entities.
   */
  readonly facadeSpanStratumAssociations: Table<DocFacadeSpanStratumAssociation>;
  readonly facadeSweepStratumAssociations: Table<DocFacadeSweepStratumAssociation>;
  readonly datumSpanAssociations: Table<DocDatumSpanAssociation>;

  /**
   * FloorSystems are collections of FloorAssembliesOverrides.
   */
  readonly floorSystems: Table<FloorSystemExternal>;
}
